import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckSquare, LogOut, RefreshCw } from 'lucide-react';
import { api, getStoredUser, getStoredToken, saveSession, clearSession, errMsg } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { toast } from 'sonner';
import { BUILD_VERSION } from '@/lib/version';
import ChangePasswordCard from '@/components/ChangePasswordCard';

const CLIENT_TZ = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ''; }
})();

export default function Profile() {
  const nav = useNavigate();
  const { lang, setLang, t, goalLabel } = useI18n();
  const [user, setUser] = useState(getStoredUser());
  const [refreshing, setRefreshing] = useState(false);

  // Real app-data refresh (NOT a page reload, NO DB writes): re-fetch the
  // current user + profile from the backend, update the stored session so
  // every screen reads fresh data, and warm the nutrition/training plans.
  const refresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const me = await api.get('/auth/me');
      const fresh = me?.data;
      const token = getStoredToken();
      if (fresh && token) { saveSession(token, fresh); setUser(fresh); }
      const id = fresh?.id || user?.id;
      if (id) {
        await Promise.allSettled([
          api.get(`/nutrition-plans/${id}?tz=${encodeURIComponent(CLIENT_TZ)}&t=${Date.now()}`),
          api.get(`/training-plans/${id}?t=${Date.now()}`),
        ]);
      }
      toast.success(t('profile.refreshed'));
    } catch (e) {
      toast.error(errMsg(e, t('common.error')));
    } finally {
      setRefreshing(false);
    }
  };

  const logout = () => {
    if (window.confirm(t('profile.logoutConfirm'))) {
      clearSession();
      window.location.href = '/';
    }
  };

  return (
    <div className="px-6 pt-10 animate-fade-in" data-testid="client-profile">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="as-label">{t('profile.label')}</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-white">
            {user?.first_name?.toUpperCase()} {user?.last_name?.toUpperCase()}
          </h1>
          <p className="mb-6 mt-1 truncate text-[13px] text-asgym-sub">{user?.email || user?.phone}</p>
        </div>
        <button
          data-testid="profile-refresh-btn"
          onClick={refresh}
          disabled={refreshing}
          title={t('profile.refresh')}
          aria-label={t('profile.refresh')}
          className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-asgym-accent/40 bg-asgym-accent/10 text-asgym-accent transition-transform active:scale-95 disabled:opacity-60"
        >
          <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="as-card divide-y divide-white/[0.05] px-4">
        <Row label={t('profile.goal')} val={user?.goal ? goalLabel(user.goal) : '—'} />
        <Row label={t('profile.startWeight')} val={user?.start_weight ? `${user.start_weight}kg` : '—'} />
        <Row label={t('profile.current')} val={user?.current_weight ? `${user.current_weight}kg` : '—'} />
        <Row label={t('profile.target')} val={user?.target_weight ? `${user.target_weight}kg` : '—'} />
      </div>

      {user?.role !== 'admin' && (
        <>
          <p className="as-section mb-2.5 mt-6">{t('profile.package')}</p>
          <div className="as-card px-4 py-4" data-testid="profile-package">
            <div className="flex items-center justify-between">
              <span className="as-label">{t('profile.package')}</span>
              <span className="rounded-lg bg-asgym-accent/10 px-2.5 py-1 text-[12px] font-extrabold text-asgym-accent" data-testid="profile-package-name">
                {user?.grandfathered ? t('pkg.grandfathered') : (user?.subscription_status === 'expired' ? t('pkg.expired') : (user?.package ? t(`pkg.${user.package}`) : t('pkg.none')))}
              </span>
            </div>
            {user?.variant && (
              <div className="mt-2 flex items-center justify-between">
                <span className="as-label">{t('profile.variant')}</span>
                <span className="text-[13px] font-bold text-white">{t(`variant.${user.variant}`)}</span>
              </div>
            )}
            {Array.isArray(user?.entitlements) && user.entitlements.length > 0 && (
              <div className="mt-3">
                <p className="as-label mb-2">{t('profile.unlockedAreas')}</p>
                <div className="flex flex-wrap gap-1.5" data-testid="profile-entitlements">
                  {user.entitlements.map((k) => (
                    <span key={k} className="rounded-lg border border-white/[0.08] bg-white/[0.03] px-2.5 py-1 text-[11px] font-bold text-asgym-sub">{t(`ent.${k}`)}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <p className="as-section mb-2.5 mt-6">{t('profile.language')}</p>
      <div className="mb-6 flex gap-2">
        {['de', 'en'].map((l) => (
          <button key={l} data-testid={`lang-${l}`} onClick={() => setLang(l)}
            className={`flex-1 rounded-xl border py-3.5 text-xs font-extrabold tracking-widest transition-colors ${lang === l ? 'border-asgym-accent/40 bg-asgym-accent/10 text-asgym-accent' : 'border-white/[0.06] bg-asgym-surface text-asgym-sub'}`}>
            {l === 'de' ? 'DEUTSCH' : 'ENGLISH'}
          </button>
        ))}
      </div>

      <p className="as-section mb-2.5 mt-6">{t('pw.section')}</p>
      <div className="mb-2.5">
        <ChangePasswordCard />
      </div>

      <button data-testid="goto-checkin" onClick={() => nav('/checkin')} className="mb-2.5 flex w-full items-center gap-4 rounded-xl border border-white/[0.06] bg-asgym-surface p-4 text-sm font-extrabold tracking-wide text-white">
        <CheckSquare size={20} className="text-asgym-accent" /> {t('profile.weeklyCheckin')}
      </button>
      <button data-testid="logout-btn" onClick={logout} className="flex w-full items-center gap-4 rounded-xl border border-asgym-accent/40 bg-asgym-surface p-4 text-sm font-extrabold tracking-wide text-asgym-accent">
        <LogOut size={20} /> {t('profile.logout')}
      </button>

      <div className="mt-8 flex flex-col items-center gap-1">
        <p className="text-[9px] font-black tracking-[1.5px] text-asgym-muted">A'S GYM · VERSION</p>
        <p className="text-[11px] font-bold text-asgym-sub">v{BUILD_VERSION}</p>
      </div>
    </div>
  );
}

const Row = ({ label, val }) => (
  <div className="flex items-center justify-between py-3">
    <span className="as-label">{label}</span>
    <span className="text-[13px] font-extrabold text-white">{val}</span>
  </div>
);
